<template>
  <td
    v-for="(question, index) in questions"
    :key="question.id"
    :class="{
      'text-center': true,
      'row-selected':
        !productBlockedInEdition?.productId && productSelected && attachmentId === productSelected,
      'in-editing-process': productBlockedInEdition?.productId && attachmentId?.toString() === productBlockedInEdition?.productId?.toString()
    }"
  >
    <condition-debug-all
      v-if="
        showHiddenQuestions || (localQuestionsConditions?.[buildId(question.id)] || {})?.visibility
      "
      :conditions="question?.conditions"
      type="Question"
      :item-id="question?.id"
    />
    <questionnaire-forms
      ref="form"
      :ref="(el) => (form[question.id] = el)"
      v-model:formData="formData[question.id]"
      :question="question"
      :attachment-id="attachmentId"
      :attachment-type="attachmentType"
      :questions="questions"
      :question-conditions="localQuestionsConditions?.[buildId(question.id)] || {}"
      :product-blocked-in-edition="productBlockedInEdition"
      @submit-form="(data) => submitField(data)"
      @product-has-invalid-answer="(pid, qid) => emit('productHasInvalidAnswer', pid, qid)"
    />
  </td>
</template>

<script setup>
import { ref, inject, watch, toRaw, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { extractFormNamesFromQuestions } from '@/stores/manager/configurationManager'
import { useStoreManager } from '@/stores/storeManager'
import { READING } from '@/constants'
import { createReadingIfNotExists } from '@/stores/manager/readingManager'
import dateHandler from '@/utils/dateHandler'
import { saveReadingCountersDebounced } from '@/stores/manager/conditionsManager'
import { ConditionsController } from '@/utils/conditions/conditionsController'
import ConditionDebugAll from '@/components/common/ConditionDebugAll.vue'
import QuestionnaireForms from './QuestionnaireForms.vue'
import { v4 as uuidv4 } from 'uuid'
import questionsHandler from '@/utils/questionsHandler'

const { t } = useI18n()

const props = defineProps({
  questions: {
    type: Object,
    required: true
  },
  groupId: {
    type: [Number, String],
    required: true
  },
  attachmentType: {
    type: String,
    required: true
  },
  attachmentId: {
    type: [Number, String],
    required: true
  },
  productSelected: {
    type: Number,
    required: false
  },
  productBlockedInEdition: {
    required: false
  },
  showMissingMandatoryAnswersFlag: {
    type: Boolean,
    required: false,
    default: false
  },
  triggerReload: {
    type: Boolean,
    required: false,
    default: false
  },
  questionsConditions: {
    type: Object,
    required: true
  }
})

const emit = defineEmits([
  'answeredToAQuestion',
  'productHasInvalidAnswer',
  'updateQuestionsConditions'
])

const storeManager = useStoreManager()
const currentMissionPlanningId = inject('currentMissionPlanningId')

const currentConfiguration = inject('currentConfiguration')
const currentPointOfSale = inject('currentPointOfSale')
const currentReadingId = inject('currentReadingId')
const foundAnswersPerGroup = inject('foundAnswersPerGroup', {})
const mission = inject('mission')
const readingAnswers = inject('readingAnswers')
const userDetails = inject('userDetails')
const showHiddenQuestions = inject('showHiddenQuestions')
const filterIsSet = inject('filterIsSet', false)
const form = ref({})
const localQuestionsConditions = ref(props.questionsConditions)

const buildId = (questionId) => {
  return questionsHandler.buildId(questionId, props.attachmentType, props.attachmentId)
}

const readingAnswersIndex = computed(() => {
  const answers = foundAnswersPerGroup.value[props.groupId]
  const index = {}

  if (!answers) return index

  for (const uuid in answers) {
    const answer = answers[uuid]
    const key = questionsHandler.buildId(
      answer.question,
      answer.attachment_type,
      answer.attachment_id
    )
    index[key] = { key: uuid, obj: answer }
  }

  return index
})

function buildLocalReadingAnswersIndex(localReadingAnswers) {
  const index = {}

  for (const uuid in localReadingAnswers) {
    const answer = localReadingAnswers[uuid]
    const key = questionsHandler.buildId(
      answer.question,
      answer.attachment_type || 'none',
      answer.attachment_id || null
    )
    index[key] = uuid
  }

  return index
}

const formData = computed(() =>
  extractFormNamesFromQuestions(
    props.questions,
    foundAnswersPerGroup.value[props.groupId],
    props.attachmentType,
    props.attachmentId
  )
)

const addNewReadingAnswer = (questionId, readingId, creationDate, value) => {
  storeManager.add('reading', 'answer', {
    reading: readingId,
    question: Number(questionId),
    value: value,
    attachment_type: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachment_id: props.attachmentId || null,
    creation_date: creationDate
  })
}

const updateReadingAnswer = (key, answer) => {
  storeManager.update('reading', 'answer', key, answer)
}

const createOrUpdateReadingAnswer = (readingId, questionId, creationDate, value) => {
  setTimeout(() => {
    const key = questionsHandler.buildId(questionId, props.attachmentType, props.attachmentId)
    const foundAnswerForQuestion = readingAnswersIndex.value[key]

    if (!foundAnswerForQuestion) {
      addNewReadingAnswer(questionId, readingId, creationDate, value)
    } else {
      const clonedFoundAnswerForQuestion = { ...foundAnswerForQuestion.obj }
      clonedFoundAnswerForQuestion.value = value
      updateReadingAnswer(foundAnswerForQuestion.key, clonedFoundAnswerForQuestion)
    }
  }, 0)
}

const createOrUpdateLocalReadingAnswer = (
  readingId,
  questionId,
  creationDate,
  localReadingAnswers,
  value
) => {
  const localIndex = buildLocalReadingAnswersIndex(localReadingAnswers)
  const key = questionsHandler.buildId(questionId, props.attachmentType, props.attachmentId)
  const foundAnswerId = localIndex[key]

  if (!foundAnswerId) {
    addNewAnswerToLocalReadingAnswers(
      questionId,
      readingId,
      creationDate,
      localReadingAnswers,
      value
    )
  } else {
    localReadingAnswers[foundAnswerId].value = value
  }
  return localReadingAnswers
}

const setLocalReadingAnswers = (readingId) => {
  return Object.keys(toRaw(readingAnswers.value)).reduce((acc, raId) => {
    if (readingAnswers.value[raId].reading === readingId) {
      acc[raId] = toRaw(readingAnswers.value[raId])
    }
    return acc
  }, {})
}

const addNewAnswerToLocalReadingAnswers = (
  questionId,
  readingId,
  creationDate,
  localReadingAnswers,
  value
) => {
  const nId = uuidv4()
  localReadingAnswers[nId] = {
    reading: readingId,
    question: Number(questionId),
    value: value,
    attachment_type: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachment_id: props.attachmentId || null,
    creation_date: creationDate
  }
}

async function submitField({ value, questionId }) {
  let readingId
  formData.value[questionId] = value
  const creationDate = dateHandler.generateNowUnixTimestamp()
  let localReadingAnswers = {}
  if (!currentReadingId.value) {
    readingId = await createReadingIfNotExists(
      '',
      mission.value?.configuration?.id,
      currentMissionPlanningId.value || null,
      userDetails.value.id,
      currentConfiguration.value?.data || null
    )
  } else {
    readingId = currentReadingId.value
  }
  localReadingAnswers = setLocalReadingAnswers(readingId)

  if (!currentReadingId.value) {
    setTimeout(() => {
      addNewReadingAnswer(questionId, readingId, creationDate, value)
    }, 0)
    addNewAnswerToLocalReadingAnswers(
      questionId,
      readingId,
      creationDate,
      localReadingAnswers,
      value
    )
  } else {
    createOrUpdateReadingAnswer(readingId, questionId, creationDate, value)
    localReadingAnswers = createOrUpdateLocalReadingAnswer(
      readingId,
      questionId,
      creationDate,
      localReadingAnswers,
      value
    )
  }
  if (props.attachmentType === READING.ATTACHMENT_TYPE.PRODUCT) {
    emit('answeredToAQuestion', { attachmentId: props.attachmentId, answers: localReadingAnswers })
  }
  launchUpdateConditions(readingId, questionId, localReadingAnswers)
}
function updateLocalQuestionsConditions(sourceJson, triggeredByQuestionId) {
  let productIsBlocked = false
  for (let key in localQuestionsConditions.value) {
    if (sourceJson.hasOwnProperty(key)) {
      const qId = key.split(':')[0]
      if (formData.value[qId] === null && sourceJson[key].mandatory && sourceJson[key].visibility) {
        productIsBlocked = true
      }
      localQuestionsConditions.value[key] = sourceJson[key]
    }
  }
  let blockingQuestions = props.productBlockedInEdition?.blockingQuestionIdList || new Set();
  if (blockingQuestions.size > 0 && blockingQuestions.has(triggeredByQuestionId?.toString()) ) {
    blockingQuestions.delete(triggeredByQuestionId?.toString())
  }
  if (blockingQuestions.size > 0 || (productIsBlocked && props.productBlockedInEdition?.productId?.toString() !== props.attachmentId?.toString()))
    emit('productHasInvalidAnswer', props.attachmentId, null)
  else if (!productIsBlocked && props.productBlockedInEdition?.productId?.toString() === props.attachmentId?.toString())
    emit('productHasInvalidAnswer', null)
}
const updateQuestionConditionsFromCalculationResult = (questions, triggeredByQuestionId) => {
  // If filter is set, we emit sync to avoid row to disappear just after filtering
  // TODO: find a better way to handle this
  if (filterIsSet.value) {
    emit('updateQuestionsConditions', questions)
  }
  updateLocalQuestionsConditions(questions, triggeredByQuestionId)
  // delay the emit to speed up response time when not filtered
  // TODO: find a better way to handle this
  if (!filterIsSet.value) {
    setTimeout(() => {
      emit('updateQuestionsConditions', questions)
    }, 0)
  }
}

const launchUpdateConditions = (readingId, questionId, localReadingAnswers) => {
  let initialContext = {
    currentValue: null,
    answers: Object.values(localReadingAnswers),
    pos: currentPointOfSale.value,
    attachementType: props.attachmentType || READING.ATTACHMENT_TYPE.NONE,
    attachementId: props.attachmentId,
    prod: null,
    nowDatetime: new Date(),
    currentQuestionId: questionId
  }
  const { questions } = new ConditionsController(
    currentConfiguration.value,
    initialContext,
    readingId
  ).updateConditions()
  updateQuestionConditionsFromCalculationResult(questions, questionId)
  saveReadingCountersDebounced(readingId, currentConfiguration.value)
}

async function focusNextMandatoryField() {
  for (const id in form.value) {
    if (form.value[id]) {
      const isValid = await form.value[id].validateForm()
      if (!isValid) {
        form.value[id].setFocus()
        break
      }
    }
  }
}

watch(
  () => props.showMissingMandatoryAnswersFlag,
  () => {
    if (props.attachmentId?.toString() !== props.productBlockedInEdition?.productId?.toString()) {
      return
    }
    focusNextMandatoryField()
  }
)
//
watch(
    () => props.triggerReload,
    () => {
      localQuestionsConditions.value = props.questionsConditions
    }
)

// watch(
//     () => props.questionsConditions,
//     () => {
//       localQuestionsConditions.value = props.questionsConditions;
//     }
// )
</script>

<style scoped>

table.sticky-header-column tbody tr:not(:last-child) td,
table.sticky-header-column tbody tr:not(:last-child) th {
  border-bottom: thin solid rgba(0,0,0,0.12);
}

table.sticky-header-column tbody td,
table.sticky-header-column tbody th {
  padding: 0 16px;
}
.screen-element-table td {
  border: 1px solid #d1d1d1;
  background-color: white;
}
</style>